import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { useAuth } from '@/contexts/AuthContext';
import { PageContainer, PageHeader } from '@/components/layout';
import { Spinner } from '@/components/ui/spinner';
import { PageTitle } from '@/components/common/PageTitle';
import { Image as ImageIcon, Loader2, CheckCircle2, Clock, AlertCircle, Trash2, RefreshCw, Copy, Check, HelpCircle, Info, FileDown, FileCode, Calendar } from 'lucide-react';
import { convertImageToBase64Smart, resizeImageBase64, isImageAccessError, KakaoCDNBlockedError } from '@/lib/imageUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { sseService } from '@/services/sseService';
import { AiUsageStatus } from '@/api/dashboardApi';
import { CustomAltEditor } from '@/components/custom-alt/CustomAltEditor';

// 날짜 유틸리티
const today = () => new Date().toISOString().slice(0, 10);
const oneMonthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};

/**
 * 이미지 오류 유형 분류
 * - CDN_BLOCKED: 이미지는 존재하지만 CDN 보안 정책으로 AI 서버 접근 차단 (예: Kakao Signed URL)
 * - IMAGE_NOT_FOUND: 이미지 자체가 존재하지 않거나 깨진 경우
 * - OTHER: 그 외 오류
 */
function classifyImageError(generatedAlt: string | null | undefined, imageSrc?: string | null): 'CDN_BLOCKED' | 'IMAGE_NOT_FOUND' | 'OTHER' {
  if (!generatedAlt) return 'OTHER';
  const hasCdnKeyword =
    generatedAlt.includes('Kakao CDN') ||
    generatedAlt.includes('Signed URL') ||
    generatedAlt.includes('CDN_ACCESS_DENIED') ||
    generatedAlt.includes('KAKAO_CDN_SIGNED_URL') ||
    generatedAlt.includes('원본 사이트에서만 접근');
  const hasSignedUrlParams =
    !!imageSrc && imageSrc.includes('credential=') && imageSrc.includes('signature=');
  if (hasCdnKeyword || hasSignedUrlParams) return 'CDN_BLOCKED';
  if (generatedAlt.includes('이미지를 불러올 수 없습니다') || generatedAlt.includes('다운로드 실패')) return 'IMAGE_NOT_FOUND';
  return 'OTHER';
}

// 우선순위 정책: Custom Alt > Generated Alt > Original Alt
function getDisplayAlt(img: ImageInfo) {
  if (img.customAltText) return { text: img.customAltText, type: 'CUSTOM' as const };
  if (img.generatedAlt) return { text: img.generatedAlt, type: 'GENERATED' as const };
  return { text: null, type: 'NONE' as const };
}

const API_BASE_URL = `${BASE}/api/crawler`;

// Axios 인스턴스 생성 (인증 토큰 자동 추가)
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터: 토큰 자동 추가
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const activeTeamId = sessionStorage.getItem('activeTeamId');
    if (activeTeamId && config.headers) {
      config.headers['X-Team-Id'] = activeTeamId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 401 에러 처리
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export type AltGenerationType = 'AI' | 'FALLBACK' | 'PENDING' | 'ERROR' | 'SKIPPED_DECORATIVE';

export interface ImageInfo {
  id: number;
  url: string;
  imageSrc: string;
  displayImageUrl?: string;
  generatedAlt: string | null;
  altGenerationType: AltGenerationType;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  updatedAt?: string;
  duplicate?: boolean;
  cachecnt?: number;  // 캐시 사용 횟수
  customAltText: string | null;  // 커스텀 ALT 텍스트
  altApprovalStatus?: string;    // 팀 ALT 승인 워크플로우 상태 (PENDING/APPROVED/REJECTED), 해당없으면 undefined
}

export interface ScanResult {
  id: number;
  url: string;
  imageSrc: string;
  displayImageUrl?: string;
  generatedAlt: string | null;
  altGenerationType: AltGenerationType;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  updatedAt?: string;
  duplicate?: boolean;
  cachecnt?: number;  // 캐시 사용 횟수
}

type RequestPhase = 'IDLE' | 'ACCEPTED' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'TIMEOUT';

const REQUEST_PHASE_TEXT: Record<RequestPhase, string> = {
  IDLE: '',
  ACCEPTED: '접수 완료: 생성 요청이 등록되었습니다.',
  PROCESSING: '처리 중: ALT 텍스트를 생성하고 있습니다. 잠시만 기다려 주세요...',
  COMPLETED: '완료: ALT 텍스트 생성이 완료되었습니다.',
  FAILED: '실패: 생성 중 오류가 발생했습니다. 재시도해 주세요.',
  TIMEOUT: '응답 지연: 서버 응답이 늦어지고 있습니다. 아래에서 직접 재시도해 주세요.',
};

type SortBy = 'CREATED_AT' | 'UPDATED_AT' | 'STATUS' | 'URL_NORMALIZED' | 'COMPLETION';
type SortDir = 'ASC' | 'DESC';
interface SortMeta { sortBy: SortBy; sortDir: SortDir; isDefault: boolean; }

interface PageResponse {
  content: ImageInfo[];
  totalPages: number;
  totalElements: number;
  number: number;
  sort?: SortMeta;
  stats?: {
    ai: number;
    fallback: number;
    pending: number;
    error: number;
    skippedDecorative: number;
    total: number;
    aiPercentage: number;
  };
}

type ImageUrlFields = {
  imageSrc: string;
  displayImageUrl?: string;
  url: string;
};

const resolveDisplayImageUrl = (
  item: ImageUrlFields,
  fallbackUrl?: string
): string => {
  const explicitDisplay = item.displayImageUrl?.trim();
  if (explicitDisplay) return explicitDisplay;

  const source = item.imageSrc?.trim();
  if (source && !source.startsWith('data:image/')) return source;

  const originalUrl = item.url?.trim();
  if (originalUrl) return originalUrl;

  if (fallbackUrl?.trim()) return fallbackUrl.trim();
  return '';
};

const normalizeImageInfo = <T extends ImageUrlFields>(item: T, fallbackUrl?: string): T => ({
  ...item,
  displayImageUrl: resolveDisplayImageUrl(item, fallbackUrl),
});

const resolveRenderableImageSrc = (item: ImageUrlFields): string => {
  const displayUrl = resolveDisplayImageUrl(item);
  if (displayUrl) return displayUrl;
  return item.imageSrc?.trim() || '';
};

const resolveDisplayImageUrlText = (item: ImageUrlFields): string => {
  return resolveDisplayImageUrl(item) || '-';
};

const sanitizeBackendErrorMessage = (message?: string): string => {
  if (!message) return '요청 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
  if (message.includes('For input string: "undefined"')) {
    return '요청 파라미터를 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
  }
  return message;
};

const REMOTE_BASE64_LIMIT_BYTES = 5 * 1024 * 1024;

const getImageActivityAt = (item: Pick<ImageInfo, 'createdAt' | 'updatedAt'>): string => {
  return item.updatedAt || item.createdAt;
};

const formatDateTime = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR');
};

const formatRelativeTime = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  const diffSec = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));

  if (diffSec < 60) return `${diffSec}초 전`;

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}분 전`;

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;

  const diffDay = Math.floor(diffHour / 24);
  return `${diffDay}일 전`;
};

const detectRemoteFileSize = async (url: string): Promise<number | null> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 1500);

  try {
    const response = await fetch(url, {
      method: 'HEAD',
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal,
    });
    const contentLength = response.headers.get('content-length');
    if (!response.ok || !contentLength) return null;

    const parsed = Number(contentLength);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

const findLatestByUrl = (items: ImageInfo[], targetUrl: string): ImageInfo | undefined => {
  const matched = items.filter(item => resolveDisplayImageUrl(item) === targetUrl);
  if (!matched.length) return undefined;
  return matched.sort((a, b) => new Date(getImageActivityAt(b)).getTime() - new Date(getImageActivityAt(a)).getTime())[0];
};

export const mergePendingScanResult = (items: ImageInfo[], pending: ScanResult | null): ImageInfo[] => {
  if (!pending) return items;
  // 2026-08-11 수정: PENDING(처리 중)뿐 아니라 duplicate(중복 히트) 응답도 강제로 끼워
  // 넣어야 한다 - 중복 히트된 기존 레코드는 기본 날짜 필터(한 달 전~오늘)보다 오래됐을 수
  // 있어, fetchImages()가 다시 조회한 목록에서 조용히 빠질 수 있다(백엔드 중복판정
  // 자체엔 날짜 제한이 없어 "이미 등록된 이미지입니다" 안내는 정상적으로 뜨는데 정작
  // 목록엔 안 보이던 프로덕션 버그).
  if (pending.status !== 'PENDING' && !pending.duplicate) return items;
  const pendingUrl = resolveDisplayImageUrl(pending);
  const exists = items.some(
    item => item.id === pending.id || (pendingUrl && resolveDisplayImageUrl(item) === pendingUrl)
  );
  if (exists) return items;
  return [pending as ImageInfo, ...items];
};

const getPhaseByStatus = (status: 'PENDING' | 'COMPLETED' | 'FAILED'): RequestPhase => {
  if (status === 'COMPLETED') return 'COMPLETED';
  if (status === 'FAILED') return 'FAILED';
  return 'PROCESSING';
};

// 백엔드 구조화 에러 응답 타입
interface AltErrorResponse {
  success: false;
  message: string;
  errorDetail: string;
  errorType?: 'KAKAO_CDN_SIGNED_URL' | 'CDN_ACCESS_DENIED' | 'NETWORK_TIMEOUT' | 'SSRF_BLOCKED' | 'UNKNOWN';
  actionRequired?: boolean;
  actionText?: string;
  actionUrl?: string;
  actionDescription?: string;
}

interface ImageRequestResponse extends ScanResult {
  success?: boolean;
  message?: string;
  duplicate?: boolean;
}

const buildImageAccessError = (errorType: AltErrorResponse['errorType']): AltErrorResponse => {
  if (errorType === 'SSRF_BLOCKED') {
    return {
      success: false,
      message: '허용되지 않은 이미지 도메인입니다.',
      errorDetail: '보안 정책상 허용되지 않은 이미지 주소입니다. 직접 업로드를 이용해 주세요.',
      errorType,
      actionRequired: true,
      actionText: '이미지 업로드로 이동',
      actionUrl: '/dashboard/crawler/upload',
      actionDescription: '허용된 외부 도메인이 아니면 서버 프록시를 사용할 수 없습니다.',
    };
  }

  if (errorType === 'NETWORK_TIMEOUT') {
    return {
      success: false,
      message: '이미지 서버 응답이 너무 느립니다.',
      errorDetail: '외부 이미지 서버 응답이 지연되어 분석할 수 없습니다. 직접 업로드를 이용해 주세요.',
      errorType,
      actionRequired: true,
      actionText: '이미지 업로드로 이동',
      actionUrl: '/dashboard/crawler/upload',
      actionDescription: '이미지를 로컬에 저장한 뒤 업로드하면 더 안정적으로 분석할 수 있습니다.',
    };
  }

  return {
    success: false,
    message: '이미지 다운로드 오류: 이미지를 불러올 수 없습니다.',
    errorDetail: 'Kakao CDN의 Signed URL 보호로 인해 원본 사이트에서만 접근 가능합니다.\n💡 해결 방법: 이미지를 다운로드 후 \'이미지 업로드\' 메뉴에서 이미지 업로드하세요.',
    errorType: 'CDN_ACCESS_DENIED',
    actionRequired: true,
    actionText: '이미지 업로드로 이동',
    actionUrl: '/dashboard/crawler/upload',
    actionDescription: '이미지를 로컬에 저장한 뒤 업로드하면 ALT 텍스트를 생성할 수 있습니다.',
  };
};

/** blob → canvas 경유 PNG ArrayBuffer 변환 */
function blobToPngBuffer(
  blob: Blob
): Promise<{ buffer: ArrayBuffer; extension: 'png' } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || 100;
      canvas.height = img.naturalHeight || 100;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0);
      try {
        canvas.toBlob(
          (pngBlob) => {
            if (!pngBlob) { resolve(null); return; }
            pngBlob.arrayBuffer()
              .then(buf => resolve({ buffer: buf, extension: 'png' }))
              .catch(() => resolve(null));
          },
          'image/png'
        );
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null); };
    img.src = objectUrl;
  });
}

/** URL → canvas 경유 PNG ArrayBuffer (crossOrigin='anonymous') */
function imgToPngBuffer(
  url: string
): Promise<{ buffer: ArrayBuffer; extension: 'png' } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || 100;
      canvas.height = img.naturalHeight || 100;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0);
      try {
        canvas.toBlob(
          (pngBlob) => {
            if (!pngBlob) { resolve(null); return; }
            pngBlob.arrayBuffer()
              .then(buf => resolve({ buffer: buf, extension: 'png' }))
              .catch(() => resolve(null));
          },
          'image/png'
        );
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function fetchImageForExcel(
  url: string,
  headers: HeadersInit = {}
): Promise<{ buffer: ArrayBuffer; extension: 'jpeg' | 'png' | 'gif' } | null> {
  // 전략 1: 직접 fetch
  try {
    const res = await fetch(url, { headers });
    if (res.ok) {
      const blob = await res.blob();
      const type = blob.type.toLowerCase();
      if (type === 'image/png') return { buffer: await blob.arrayBuffer(), extension: 'png' };
      if (type.includes('jpeg') || type.includes('jpg')) return { buffer: await blob.arrayBuffer(), extension: 'jpeg' };
      if (type === 'image/gif') return { buffer: await blob.arrayBuffer(), extension: 'gif' };
      return await blobToPngBuffer(blob);
    }
  } catch {
    // CORS 차단 → 전략 2로
  }

  // 전략 2: <img crossOrigin='anonymous'> + canvas
  const v2 = await imgToPngBuffer(url);
  if (v2) return v2;

  // 전략 3: 백엔드 프록시 경유
  try {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const proxyUrl = `${BASE}/api/crawler/image-proxy?url=${encodeURIComponent(url)}`;
    const proxyHeaders: HeadersInit = {};
    if (token) (proxyHeaders as Record<string, string>).Authorization = `Bearer ${token}`;
    const res = await fetch(proxyUrl, { headers: proxyHeaders });
    if (res.ok) {
      const blob = await res.blob();
      const type = blob.type.toLowerCase();
      if (type === 'image/png') return { buffer: await blob.arrayBuffer(), extension: 'png' };
      if (type.includes('jpeg') || type.includes('jpg')) return { buffer: await blob.arrayBuffer(), extension: 'jpeg' };
      if (type === 'image/gif') return { buffer: await blob.arrayBuffer(), extension: 'gif' };
      return await blobToPngBuffer(blob);
    }
  } catch {
    // 프록시 미구현 또는 에러 → null 반환
  }

  return null;
}

function AlttextImgUrl() {
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const { user } = useAuth();
  const isSuperAdmin = user?.userRole === 'SUPER_ADMIN';
  const location = useLocation();
  const [imageUrl, setImageUrl] = useState(() => (location.state as any)?.prefillUrl ?? '');
  const [processing, setProcessing] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [images, setImages] = useState<ImageInfo[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'COMPLETED'>('ALL');
  const [allStats, setAllStats] = useState<{ ai: number; fallback: number; pending: number; error: number; skippedDecorative: number; total: number; aiPercentage: number } | null>(null);
  const [error, setError] = useState<string | AltErrorResponse | null>(null);
  const navigate = useNavigate();
  const [altTypeFilter, setAltTypeFilter] = useState<AltGenerationType | 'ALL'>('ALL');
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [copiedHtmlId, setCopiedHtmlId] = useState<number | null>(null);
  const [regeneratingIds, setRegeneratingIds] = useState<Set<number>>(new Set());
  // 브라우저에서도 이미지 로드 실패한 ID 추적 (서버 접근 차단 vs 실제 없는 이미지 구분용)
  const [browserFailedImages, setBrowserFailedImages] = useState<Set<number>>(new Set());
  const [showGuide, setShowGuide] = useState(false);
  const [aiUsage, setAiUsage] = useState<AiUsageStatus | null>(null);
  const [useContextProfile, setUseContextProfile] = useState(false);
  const [profileActive, setProfileActive] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [tick, setTick] = useState(0); // PENDING 타임아웃 감지용 리렌더 트리거
  const [lastStrategy, setLastStrategy] = useState<'server-fetch' | 'client-base64' | null>(null);
  const prevPendingCountRef = useRef<number>(0); // 이전 pending 수 (완료 감지용)
  const [allCompletedBanner, setAllCompletedBanner] = useState(false); // 모두 완료 배너
  const [requestPhase, setRequestPhase] = useState<RequestPhase>('IDLE');
  const [duplicateNotice, setDuplicateNotice] = useState<string | null>(null);
  const [highlightedImageId, setHighlightedImageId] = useState<number | null>(null);
  const processingLockRef = useRef(false);
  const [sortBy, setSortBy] = useState<SortBy>('UPDATED_AT'); // 이미지 URL 기본: 수정일 DESC
  const [sortDir, setSortDir] = useState<SortDir>('DESC');
  const [sortIsDefault, setSortIsDefault] = useState(true);
  const [startDate, setStartDate] = useState(oneMonthAgo());
  const [endDate, setEndDate] = useState(today());

  const fetchAiUsage = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(`${BASE}/api/dashboard/ai-usage-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAiUsage(response.data);
    } catch (err) {
      // 사용량 조회 실패 시 무시
    }
  };

  const fetchProfileStatus = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(`${BASE}/api/user/alt-profiles/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const { hasProfile, useYn } = response.data;
      setProfileActive(hasProfile);
      setUseContextProfile(hasProfile && useYn === 'Y');
    } catch {
      // 프로필 상태 조회 실패 시 무시
    }
  };

  // 복사 함수
  const handleCopyAlt = async (text: string, imageId: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(imageId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      alert('복사하지 못했습니다.');
    }
  };

  const handleCopyHtml = async (img: ImageInfo) => {
    try {
      const htmlSrc = resolveDisplayImageUrl(img);
      if (!htmlSrc) {
        alert('복사할 원본 이미지 URL이 없습니다.');
        return;
      }
      const displayAlt = getDisplayAlt(img);
      const htmlTag = `<img src="${htmlSrc}" alt="${displayAlt.text || ''}" />`;
      await navigator.clipboard.writeText(htmlTag);
      setCopiedHtmlId(img.id);
      setTimeout(() => setCopiedHtmlId(null), 2000);
    } catch (err) {
      alert('복사하지 못했습니다.');
    }
  };

  // 커스텀 ALT 저장 핸들러
  const handleCustomAltSaved = (imageId: number, customAltText: string) => {
    setImages(images.map(img => 
      img.id === imageId 
        ? { ...img, customAltText }
        : img
    ));
  };

  // 커스텀 ALT 삭제 핸들러
  const handleCustomAltDeleted = (imageId: number) => {
    setImages(images.map(img => 
      img.id === imageId 
        ? { ...img, customAltText: null }
        : img
    ));
  };

  const handleDoubleClick = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setError('복사되었습니다!');
      setTimeout(() => setError(null), 1500);
    } catch (err) {
      // 복사 실패 시 무시
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!window.confirm('이 이미지를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await apiClient.delete(`/images/${imageId}`);
      fetchImages(currentPage, filter === 'ALL' ? null : filter);
      alert('이미지가 삭제되었습니다.');
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('삭제 API가 구현되지 않았습니다. 백엔드 개발이 필요합니다.');
      } else {
        setError('이미지를 삭제하지 못했습니다.');
      }
    }
  };

  const handleExcelDownload = async () => {
    setExcelLoading(true);
    try {
      const statusParam = filter !== 'ALL' ? `&status=${filter}` : '';

      // 전체 페이지 순차 fetch (size=9999 대신 안정적인 배치 크기 사용)
      let allData: ImageInfo[] = [];
      let fetchPage = 0;
      while (true) {
        const batchRes = await apiClient.get<PageResponse>(
          `/images?page=${fetchPage}&size=999999&sourceType=imageUrl${statusParam}`
        );
        allData = allData.concat(batchRes.data.content);
        if (fetchPage >= batchRes.data.totalPages - 1 || batchRes.data.content.length === 0) break;
        fetchPage++;
      }
      // 목록에 표시되는 것과 동일하게 altTypeFilter 적용
      const data = altTypeFilter === 'ALL' ? allData : allData.filter(img => img.altGenerationType === altTypeFilter);

      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('이미지목록');
      ws.columns = [
        { header: 'ID', key: 'id', width: 8 },
        { header: '이미지 URL', key: 'imageUrl', width: 55 },
        { header: 'AI 생성 ALT', key: 'alt', width: 50 },
        { header: '생성 방식', key: 'type', width: 15 },
        { header: '상태', key: 'status', width: 12 },
        { header: '최종 수정일', key: 'updatedAt', width: 22 },
      ];
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).height = 18;

      for (let i = 0; i < data.length; i++) {
        const img = data[i];
        const imgUrl = resolveDisplayImageUrl(img);
        const row = ws.addRow({
          id: img.id,
          imageUrl: imgUrl,
          alt: img.generatedAlt ?? '',
          type: img.altGenerationType ?? '',
          status: img.status ?? '',
          updatedAt: formatDateTime(getImageActivityAt(img)),
        });
        if (imgUrl) {
          row.getCell('imageUrl').value = { text: imgUrl, hyperlink: imgUrl };
          row.getCell('imageUrl').font = { color: { argb: 'FF0563C1' }, underline: true };
        }
      }

      const buf = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `이미지URL_목록_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch {
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setExcelLoading(false);
    }
  };

  const fetchStats = async (status: string | null = null, altGenerationType: AltGenerationType | 'ALL' | null = null) => {
    try {
      const statusParam = status && status !== 'ALL' ? `&status=${status}` : '';
      const altTypeParam = altGenerationType && altGenerationType !== 'ALL' ? `&altGenerationType=${altGenerationType}` : '';
      const sourceTypeParam = '&sourceType=imageUrl';
      const channelParam = '&channel=INNER';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await apiClient.get<PageResponse>(
        `/images?page=0&size=999999${statusParam}${altTypeParam}${sourceTypeParam}${channelParam}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      // 백엔드 stats 필드 그대로 사용
      if (res.data.stats) {
        setAllStats(res.data.stats);
      }
    } catch {
      // 통계 조회 실패는 무시
    }
  };

  const fetchImages = async (page = 0, status: string | null = null, size = pageSize, sb?: SortBy, sd?: SortDir, altGenerationType?: AltGenerationType | 'ALL' | null, overrideStartDate?: string, overrideEndDate?: string) => {
    try {
      const effectiveSortBy = sb ?? sortBy;
      const effectiveSortDir = sd ?? sortDir;
      const statusParam = status && status !== 'ALL' ? `&status=${status}` : '';
      const effectiveAltType = altGenerationType ?? altTypeFilter;
      const altTypeParam = effectiveAltType && effectiveAltType !== 'ALL' ? `&altGenerationType=${effectiveAltType}` : '';
      const sourceTypeParam = '&sourceType=imageUrl'; // 이미지 URL 크롤링만 필터링
      const channelParam = '&channel=INNER'; // INNER 채널만 필터링
      const sortParam = `&sortBy=${effectiveSortBy}&sortDir=${effectiveSortDir}`;
      const effectiveStartDate = overrideStartDate ?? startDate;
      const effectiveEndDate = overrideEndDate ?? endDate;
      const startDateParam = effectiveStartDate ? `&startDate=${effectiveStartDate}` : '';
      const endDateParam = effectiveEndDate ? `&endDate=${effectiveEndDate}` : '';
      const response = await apiClient.get<PageResponse>(
        `/images?page=${page}&size=${size}${statusParam}${altTypeParam}${sourceTypeParam}${channelParam}${sortParam}${startDateParam}${endDateParam}`
      );
      const normalized = response.data.content.map(img => normalizeImageInfo(img));
      const merged = mergePendingScanResult(normalized, scanResult);
      setImages(merged);
      setTotalPages(response.data.totalPages);
      setTotalElements((response.data.totalElements ?? 0) + (merged.length > normalized.length ? 1 : 0));
      setCurrentPage(page);
      if (response.data.sort) setSortIsDefault(response.data.sort.isDefault);
      // fetchImages 응답의 stats 재사용 (불필요한 API 호출 제거)
      if (response.data.stats) {
        setAllStats(response.data.stats);
      }

      // 상단 처리 결과 카드와 목록 상태를 동기화
      if (scanResult) {
        const sameId = normalized.find(img => img.id === scanResult.id);
        const scanUrl = resolveDisplayImageUrl(scanResult);
        const sameUrlLatest = !sameId && scanUrl
          ? findLatestByUrl(normalized, scanUrl)
          : undefined;
        const synced = sameId ?? sameUrlLatest;
        if (synced) {
          setScanResult(synced);
          setRequestPhase(getPhaseByStatus(synced.status));
          if (synced.status === 'COMPLETED') {
            setError(null);
          }
        }
      }
    } catch (err) {
      setError('이미지 목록을 불러오지 못했습니다.');
    }
  };

  useEffect(() => {
    fetchImages(0, filter === 'ALL' ? null : filter);
    fetchAiUsage();
    fetchProfileStatus();
  }, []);

  // PENDING 항목이 30초 경과 후 타임아웃 UI로 전환되도록 리렌더 트리거
  useEffect(() => {
    const hasFreshPending = images.some(img => img.status === 'PENDING') || (!!scanResult && scanResult.status === 'PENDING');
    if (!hasFreshPending) return;
    const timer = setTimeout(() => setTick(t => t + 1), 35_000);
    return () => clearTimeout(timer);
  }, [images, scanResult?.status]);

  // scanResult가 PENDING 상태이면 SSE 우선, 실패 시 단건 폴링으로 상태를 동기화
  useEffect(() => {
    if (!scanResult || scanResult.status !== 'PENDING') return;
    const targetId = scanResult.id;
    setRequestPhase(prev => (prev === 'ACCEPTED' ? 'PROCESSING' : prev));
    let attempts = 0;
    let cancelled = false;
    let serverErrorRetries = 0;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    const controller = new AbortController();

    const applyUpdatedImage = (updated: ImageInfo) => {
      if (cancelled) return;
      setScanResult(updated);
      setImages(prev => {
        const exists = prev.some(img => img.id === updated.id);
        if (exists) {
          return prev.map(img => img.id === updated.id ? updated : img);
        }
        return [updated, ...prev];
      });

      if (updated.status === 'COMPLETED') {
        setRequestPhase('COMPLETED');
        setError(null);
        fetchAiUsage();
      } else if (updated.status === 'FAILED') {
        setRequestPhase('FAILED');
      }
    };

    const poll = async () => {
      if (cancelled || !targetId || targetId <= 0) return;
      attempts++;
      if (attempts > 20) return;

      let delayOverride: number | null = null;

      try {
        const res = await apiClient.get<ImageInfo>(`/images/${targetId}`);
        serverErrorRetries = 0;
        const updated = normalizeImageInfo(res.data);
        applyUpdatedImage(updated);
        if (updated.status !== 'PENDING') {
          return;
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status >= 500) {
          serverErrorRetries++;
          if (serverErrorRetries < 3) {
            delayOverride = 1000 * (2 ** (serverErrorRetries - 1));
          }
        }

        if (status >= 500 && serverErrorRetries >= 3) {
          if (!cancelled) {
            setRequestPhase('FAILED');
            setScanResult(prev => (prev ? { ...prev, status: 'FAILED', altGenerationType: 'ERROR' } : prev));
            setError('서버에서 이미지 상태를 조회하지 못했습니다. 잠시 후 다시 시도해 주세요.');
          }
          return;
        }
      }

      if (!cancelled && attempts < 20) {
        const delay = delayOverride ?? (attempts < 3 ? 1000 : 3000);
        fallbackTimer = setTimeout(poll, delay);
      } else if (!cancelled) {
        // 폴링 소진 — 백엔드가 응답하지 않음 → TIMEOUT 단계로 전환
        setRequestPhase('TIMEOUT');
      }
    };

    const subscribeToStatusStream = async () => {
      if (!targetId || targetId <= 0) return;

      try {
        let streamSettled = false;
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        const headers: HeadersInit = { Accept: 'text/event-stream' };
        if (token) {
          (headers as Record<string, string>).Authorization = `Bearer ${token}`;
        }
        const activeTeamId = sessionStorage.getItem('activeTeamId');
        if (activeTeamId) {
          (headers as Record<string, string>)['X-Team-Id'] = activeTeamId;
        }

        const response = await fetch(`${API_BASE_URL}/images/${targetId}/status-stream`, {
          headers,
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const handleEventBlock = (block: string) => {
          const lines = block.split(/\r?\n/);
          let eventName = 'message';
          const dataLines: string[] = [];

          lines.forEach((line) => {
            if (line.startsWith('event:')) {
              eventName = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trim());
            }
          });

          if (dataLines.length === 0) return;
          const payloadText = dataLines.join('\n');

          if (eventName === 'timeout') {
            setRequestPhase('TIMEOUT');
            return;
          }

          if (eventName !== 'status-update') return;

          try {
            const payload = JSON.parse(payloadText);
            const updated = normalizeImageInfo(payload.data ?? payload);
            applyUpdatedImage(updated);
            if (updated.status !== 'PENDING') {
              streamSettled = true;
              controller.abort();
            }
          } catch {
            // ignore malformed event payload
          }
        };

        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split(/\r?\n\r?\n/);
          buffer = chunks.pop() || '';
          chunks.forEach(handleEventBlock);
        }

        if (buffer.trim()) {
          handleEventBlock(buffer.trim());
        }

        if (!cancelled && !streamSettled) {
          fallbackTimer = setTimeout(poll, 1000);
        }
      } catch {
        if (!cancelled) {
          fallbackTimer = setTimeout(poll, 1000);
        }
      }
    };

    if (targetId > 0) {
      subscribeToStatusStream();
    }

    // 클라이언트 측 타임아웃: 60초 내 완료되지 않으면 SSE 강제 종료 → TIMEOUT 전환
    // B/E SSE는 5분 유지되지만 사용자가 그 동안 기다리지 않도록 함
    const clientTimeoutTimer = setTimeout(() => {
      if (!cancelled) {
        controller.abort();
        setRequestPhase('TIMEOUT');
      }
    }, 60_000);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(clientTimeoutTimer);
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
    };
  }, [scanResult?.id, scanResult?.status]);

  // 목록에 실제 PENDING 항목이 있는지 여부 (images 참조가 바뀌어도 값이 같으면 effect 재실행 안 함)
  const hasPendingItems = images.some(img => img.status === 'PENDING');

  // 목록에 PENDING이 있으면 주기적으로 목록 갱신 (scanResult PENDING은 위 effect가 담당 → 중복 방지)
  useEffect(() => {
    // scanResult가 PENDING이면 size=200 effect가 이미 폴링 중 → 이 effect는 건너뜀
    if (scanResult?.status === 'PENDING') return;
    if (!hasPendingItems) return;

    const timer = setInterval(() => {
      fetchImages(currentPage, filter === 'ALL' ? null : filter);
    }, 4000);

    return () => clearInterval(timer);
  }, [hasPendingItems, scanResult?.status, currentPage, filter, pageSize]);

  // 이미지가 생성된 지 30초 이상 경과하여 PENDING 상태이면 true
  const isLongPending = (createdAt: string): boolean => {
    try { return Date.now() - new Date(createdAt).getTime() > 90_000; }
    catch { return false; }
  };

  const handleProcessImage = async () => {
    if (processingLockRef.current) {
      return;
    }
    processingLockRef.current = true;

    const requestUrl = imageUrl.trim();

    if (!requestUrl) {
      setError('이미지 URL을 입력해주세요.');
      processingLockRef.current = false;
      return;
    }

    // URL 유효성 검사
    try {
      const parsedUrl = new URL(requestUrl);
      if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
        setError('이미지 URL은 http:// 또는 https:// 형식만 사용할 수 있습니다.');
        processingLockRef.current = false;
        return;
      }
    } catch {
      setError('올바른 이미지 URL을 입력해주세요.');
      processingLockRef.current = false;
      return;
    }

    setProcessing(true);
    setScanResult(null);
    setError(null);
    setDuplicateNotice(null);
    setLastStrategy(null);
    setRequestPhase('IDLE');

    // 무료 플랜 일일 한도 체크
    if (aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0)) {
      alert(`무료 플랜은 하루 최대 ${aiUsage.dailyLimit}개의 ALT 텍스트를 생성할 수 있습니다.\n제한 없이 이용하시려면 스타트 플랜 이상으로 업그레이드해 주세요.`);
      setProcessing(false);
      processingLockRef.current = false;
      return;
    }
    if (aiUsage) {
      // 팀 컨텍스트인 경우 팀 한도 기준으로 체크
      if (aiUsage.isTeamContext) {
        const teamLimit = aiUsage.teamLimit ?? aiUsage.limit;
        const teamUsed = aiUsage.teamTotalUsed ?? aiUsage.used;
        if (teamUsed >= teamLimit) {
          setError(`팀 AI 사용 한도(${teamLimit}건)를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.`);
          setProcessing(false);
          processingLockRef.current = false;
          return;
        }
      } else if (!aiUsage.canUse) {
        setError(`이번 달 AI 사용 한도(${aiUsage.limit}건)를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.`);
        setProcessing(false);
        processingLockRef.current = false;
        return;
      }
    }

    // 요청 즉시 상단 카드 기준을 새 URL로 전환해 이전 결과가 남지 않도록 한다.
    const optimisticPending: ScanResult = {
      id: -Date.now(),
      url: requestUrl,
      imageSrc: requestUrl,
      displayImageUrl: requestUrl,
      generatedAlt: null,
      altGenerationType: 'PENDING',
      status: 'PENDING',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setScanResult(optimisticPending);
    setRequestPhase('ACCEPTED');

    try {
      const response = await apiClient.post<ImageRequestResponse>('/image-url', {
        imageUrl: requestUrl,
        useContextProfile: useContextProfile,
      });

      // CloudFront가 S3 HTML을 반환한 경우 감지 (서버 라우팅 오류)
      if (typeof response.data === 'string' || !response.data) {
        throw new Error('서버 응답 오류: 잠시 후 다시 시도해 주세요.');
      }

      const responseStatus = response.data.status ?? 'PENDING';
      const acceptedResult: ScanResult = normalizeImageInfo({
        ...response.data,
        generatedAlt: response.data.generatedAlt ?? null,
        altGenerationType: response.data.altGenerationType ?? (
          responseStatus === 'COMPLETED' ? 'AI' :
          responseStatus === 'FAILED' ? 'ERROR' : 'PENDING'
        ),
        status: responseStatus,
        createdAt: response.data.createdAt || new Date().toISOString(),
        updatedAt: response.data.updatedAt || response.data.createdAt || new Date().toISOString(),
        duplicate: response.data.duplicate,
      }, requestUrl);

      setScanResult(acceptedResult);

      // 케이스 1: COMPLETED → 즉시 표시, SSE/폴링 시작하지 않음
      // 케이스 2: FAILED   → 재시도 버튼 표시
      // 케이스 3: PENDING  → SSE 연결 (60초 클라이언트 타임아웃)
      setRequestPhase(
        responseStatus === 'PENDING' ? 'ACCEPTED' : getPhaseByStatus(responseStatus as 'COMPLETED' | 'FAILED')
      );

      if (responseStatus === 'FAILED' && !response.data.generatedAlt) {
        setError(response.data.message || 'ALT 생성에 실패했습니다. 재시도해 주세요.');
      }

      if (response.data.duplicate) {
        setDuplicateNotice('이미 등록된 이미지입니다. 기존 결과를 확인해 주세요.');
        setHighlightedImageId(acceptedResult.id);
        window.setTimeout(() => {
          setHighlightedImageId((current) => current === acceptedResult.id ? null : current);
        }, 4000);
      }
      // ✅ 이전에 골라둔 상태/유형 탭(altTypeFilter)이 ALL이 아니면 방금 처리된
      // 결과가 그 탭 조건과 안 맞아 목록에서 보이지 않는 문제가 있었다 - 새로 처리한
      // 결과는 필터와 무관하게 항상 보이도록 두 탭을 ALL로 되돌리고 그 값으로 조회한다.
      setFilter('ALL');
      setAltTypeFilter('ALL');
      fetchImages(0, null, pageSize, undefined, undefined, 'ALL');
      fetchAiUsage();
      setImageUrl('');
    } catch (err: any) {
      setRequestPhase('FAILED');
      // POST 실패 시 scanResult를 FAILED로 전환해 폴링 effect가 즉시 멈추도록 함
      setScanResult(prev => prev ? { ...prev, status: 'FAILED', altGenerationType: 'ERROR' } : null);
      if (err.response?.status === 429) {
        // 백엔드가 errorType: 'QUOTA_EXCEEDED'(월간 한도)를 명시하는 경우, FREE 플랜이라도
        // "하루 최대 20개" 문구를 보여주면 안 됨 - 일별 한도는 남았지만 월간 한도가 초과된
        // 상태일 수 있음 (일별 20건 x 2일 등으로 월간 40건에 먼저 도달하는 경우).
        if (err.response?.data?.errorType === 'QUOTA_EXCEEDED') {
          setError(err.response?.data?.message || '이번 달 AI 사용 한도를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.');
        } else if (aiUsage?.plan === 'FREE') {
          alert(`무료 플랜은 하루 최대 20개의 ALT 텍스트를 생성할 수 있습니다.\n제한 없이 이용하시려면 스타트 플랜 이상으로 업그레이드해 주세요.`);
        } else {
          setError(err.response?.data?.message || '이번 달 AI 사용 한도를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.');
        }
        fetchAiUsage();
        return;
      }

      const status = err.response?.status ?? 0;
      const data = err.response?.data;
      const errorType = data?.errorType;

      if (errorType === 'SSRF_BLOCKED' || errorType === 'NETWORK_TIMEOUT') {
        setError(buildImageAccessError(errorType));
        return;
      }

      // 이미지 접근 제한 에러 → 브라우저에서도 접근 불가 시 업로드 유도
      if (isImageAccessError(status, errorType)) {
        console.log('[ALT] 이미지 접근 실패 (status:', status, 'errorType:', errorType, ') → 업로드 유도');
        setError(buildImageAccessError('CDN_ACCESS_DENIED'));
        return;
      }

      // 그 외 에러: 백엔드 구조화 응답 또는 일반 메시지 표시
      if (data && data.success === false && data.errorDetail) {
        setError(data as AltErrorResponse);
      } else {
        setError(data?.message || '이미지 처리 중 오류가 발생했습니다.');
      }
    } finally {
      setProcessing(false);
      processingLockRef.current = false;
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('모든 이미지 데이터를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await apiClient.delete('/images/clear?sourceType=imageUrl');
      setScanResult(null);
      setImages([]);
      setTotalPages(0);
      setTotalElements(0);
      setCurrentPage(0);
      setError(null);
      alert('모든 데이터가 삭제되었습니다.');
    } catch (err) {
      setError('데이터를 삭제하지 못했습니다.');
    }
  };

  const handleSearch = () => {
    const statusFilter = filter === 'ALL' ? null : filter;
    fetchImages(0, statusFilter, pageSize, undefined, undefined, undefined, startDate, endDate);
  };

  const handleRegenerateAlt = async (imageId: number, forceRefresh = false) => {
    // ── ID 보정: POST 응답에 id가 없거나 optimistic(-) ID인 경우 images 목록에서 실제 ID 조회 ──
    let resolvedId: number = imageId;
    if (!resolvedId || resolvedId < 0) {
      const targetUrl = scanResult ? resolveDisplayImageUrl(scanResult) : null;
      const found = targetUrl ? findLatestByUrl(images, targetUrl) : undefined;
      if (found && found.id > 0) {
        resolvedId = found.id;
        setScanResult(found);
      } else {
        // images 목록에서 못 찾으면 서버에서 직접 조회
        try {
          const res = await apiClient.get<PageResponse>('/images?page=0&size=200&sourceType=imageUrl');
          const normalizedContent = res.data.content.map(img => normalizeImageInfo(img));
          const fromServer = targetUrl ? findLatestByUrl(normalizedContent, targetUrl) : undefined;
          if (fromServer && fromServer.id > 0) {
            resolvedId = fromServer.id;
            setScanResult(fromServer);
            setImages(prev => {
              const ids = new Set(prev.map(i => i.id));
              return ids.has(fromServer.id) ? prev : [fromServer, ...prev];
            });
          }
        } catch {
          // 무시
        }
      }
      if (!resolvedId || resolvedId < 0) {
        setError('이미지 ID를 확인할 수 없습니다. 목록을 새로고침하고 다시 시도해 주세요.');
        return;
      }
    }

    if (aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0)) {
      setError(`⚠️ 오늘 ${aiUsage.dailyUsed ?? 0}/${aiUsage.dailyLimit}건 — 일일 한도 초과. 내일 다시 이용하거나 플랜을 업그레이드하세요.`);
      return;
    }
    
    // forceRefresh인 경우 새로운 엔드포인트 사용
    if (forceRefresh) {
      if (!window.confirm('이미 AI로 생성한 ALT를 다시 생성하여 ALT를 업데이트 하시겠습니까? (AI 사용량이 차감됩니다)')) {
        return;
      }

      setRegeneratingIds(prev => new Set(prev).add(resolvedId));
      try {
        const response = await apiClient.post(`/images/${resolvedId}/force-refresh`);

        // SSE 연결로 실시간 상태 수신
        sseService.connect(resolvedId, {
          onConnected: (data) => {
            console.log('[SSE] 강제 재분석 연결됨:', data);
          },
          onStatusUpdate: (data) => {
            if (data.status === 'FAILED' && data.errorType) {
              // 에러 코드에 따른 알림 표시
              let errorMessage = '이미지 분석에 실패했습니다.';
              if (data.errorType === 'MONTHLY_LIMIT_EXCEEDED') {
                errorMessage = '월별 한도를 초과했습니다. 요금제 업그레이드를 진행해 주세요.';
              } else if (data.errorType === 'DAILY_LIMIT_EXCEEDED') {
                errorMessage = '일별 한도를 초과했습니다. 내일 다시 시도해주세요.';
              } else if (data.errorType === 'ENTERPRISE_LIMIT_EXCEEDED') {
                errorMessage = '엔터프라이즈 한도를 초과했습니다. 엔터프라이즈 계약서 수정으로 이용가능한 건수를 추가해 주세요.';
              }
              alert(errorMessage);
            }

            if (data.status === 'COMPLETED' || data.status === 'FAILED') {
              const statusParam = filter !== 'ALL' ? `&status=${filter}` : '';
              apiClient.get<PageResponse>(
                `/images?page=${currentPage}&size=${pageSize}&sourceType=imageUrl${statusParam}`
              ).then(res => {
                const normalized = res.data.content.map(img => normalizeImageInfo(img));
                setImages(normalized);
                setTotalPages(res.data.totalPages);
                setTotalElements(res.data.totalElements ?? 0);
                fetchAiUsage();
                setRegeneratingIds(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(resolvedId);
                  return newSet;
                });
                sseService.disconnect();
              });
            }
          },
          onTimeout: (data) => {
            console.log('[SSE] 타임아웃:', data);
            const statusParam = filter !== 'ALL' ? `&status=${filter}` : '';
            apiClient.get<PageResponse>(
              `/images?page=${currentPage}&size=${pageSize}&sourceType=imageUrl${statusParam}`
            ).then(res => {
              const normalized = res.data.content.map(img => normalizeImageInfo(img));
              setImages(normalized);
              setTotalPages(res.data.totalPages);
              setTotalElements(res.data.totalElements ?? 0);
              fetchAiUsage();
              setRegeneratingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(resolvedId);
                return newSet;
              });
            });
          },
          onError: (error) => {
            console.error('[SSE] 에러:', error);
            const statusParam = filter !== 'ALL' ? `&status=${filter}` : '';
            apiClient.get<PageResponse>(
              `/images?page=${currentPage}&size=${pageSize}&sourceType=imageUrl${statusParam}`
            ).then(res => {
              const normalized = res.data.content.map(img => normalizeImageInfo(img));
              setImages(normalized);
              setTotalPages(res.data.totalPages);
              setTotalElements(res.data.totalElements ?? 0);
              fetchAiUsage();
              setRegeneratingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(resolvedId);
                return newSet;
              });
            });
          }
        });
      } catch (err: any) {
        alert(err.response?.data?.error || 'ALT 강제 재생성 실패');
        setRegeneratingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(resolvedId);
          return newSet;
        });
      }
      return;
    }
    
    // 기존 재분석 로직
    if (!window.confirm('AI를 통한 ALT 생성을 다시 시도하시겠습니까?')) {
      return;
    }

    // 로딩 상태 추가
    setRegeneratingIds(prev => new Set(prev).add(resolvedId));
    const selected = images.find(i => i.id === resolvedId);
    if (selected) {
      setScanResult({ ...selected, status: 'PENDING', altGenerationType: 'PENDING' });
    }
    setRequestPhase('PROCESSING');

    try {
      // 브라우저에서 Base64 변환 시도 (Kakao Signed URL 등 서버 다운로드 실패 대비)
      const image = images.find(i => i.id === resolvedId);
      const imageSrc = image?.displayImageUrl ?? image?.imageSrc ?? '';
      let imageBase64: string | null = null;
      if (imageSrc) {
        console.log('[재분석] Base64 변환 시도 (직접 + 프록시):', imageSrc);
        imageBase64 = await convertImageToBase64Smart(imageSrc, BASE);
        if (imageBase64 && imageBase64.length > 10_000_000) {
          imageBase64 = await resizeImageBase64(imageBase64, 1920, 1080, 0.85);
        }
        if (imageBase64) {
          console.log('[재분석] Base64 변환 성공');
        } else {
          console.warn('[재분석] Base64 변환 실패 → 서버 다운로드 fallback');
        }
      }
      // 백엔드 @RequestParam 수신: application/x-www-form-urlencoded 방식으로 전송
      const params = new URLSearchParams();
      params.append('useContextProfile', useContextProfile ? 'true' : 'false');
      if (imageBase64) params.append('imageBase64', imageBase64);
      await apiClient.post(
        `/images/${resolvedId}/regenerate`,
        params.toString(),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      // SSE 연결로 실시간 상태 수신
      sseService.connect(resolvedId, {
        onConnected: (data) => {
          console.log('[SSE] 재분석 연결됨:', data);
        },
        onStatusUpdate: (data) => {
          if (data.status === 'FAILED' && data.errorType) {
            // 에러 코드에 따른 알림 표시
            let errorMessage = '이미지 분석에 실패했습니다.';
            if (data.errorType === 'MONTHLY_LIMIT_EXCEEDED') {
              errorMessage = '월별 한도를 초과했습니다. 요금제 업그레이드를 진행해 주세요.';
            } else if (data.errorType === 'DAILY_LIMIT_EXCEEDED') {
              errorMessage = '일별 한도를 초과했습니다. 내일 다시 시도해주세요.';
            } else if (data.errorType === 'ENTERPRISE_LIMIT_EXCEEDED') {
              errorMessage = '엔터프라이즈 한도를 초과했습니다. 엔터프라이즈 계약서 수정으로 이용가능한 건수를 추가해 주세요.';
            }
            alert(errorMessage);
          }

          if (data.status === 'COMPLETED' || data.status === 'FAILED') {
            const statusParam = filter !== 'ALL' ? `&status=${filter}` : '';
            apiClient.get<PageResponse>(
              `/images?page=${currentPage}&size=${pageSize}&sourceType=imageUrl${statusParam}`
            ).then(res => {
              const normalized = res.data.content.map(img => normalizeImageInfo(img));
              setImages(normalized);
              setTotalPages(res.data.totalPages);
              setTotalElements(res.data.totalElements ?? 0);

              const updated = normalized.find(img => img.id === resolvedId);
              if (updated) {
                setScanResult(updated);
                setRequestPhase(getPhaseByStatus(updated.status));
                if (updated.status === 'COMPLETED') {
                  setError(null);
                }
              }
              setRegeneratingIds(prev => {
                const next = new Set(prev);
                next.delete(resolvedId);
                return next;
              });
              sseService.disconnect();
            });
          }
        },
        onTimeout: (data) => {
          console.log('[SSE] 타임아웃:', data);
          const statusParam = filter !== 'ALL' ? `&status=${filter}` : '';
          apiClient.get<PageResponse>(
            `/images?page=${currentPage}&size=${pageSize}&sourceType=imageUrl${statusParam}`
          ).then(res => {
            const normalized = res.data.content.map(img => normalizeImageInfo(img));
            setImages(normalized);
            setTotalPages(res.data.totalPages);
            setTotalElements(res.data.totalElements ?? 0);

            const updated = normalized.find(img => img.id === resolvedId);
            if (updated) {
              setScanResult(updated);
              setRequestPhase(getPhaseByStatus(updated.status));
              if (updated.status === 'COMPLETED') {
                setError(null);
              }
            }
            setRegeneratingIds(prev => {
              const next = new Set(prev);
              next.delete(resolvedId);
              return next;
            });
          });
        },
        onError: (error) => {
          console.error('[SSE] 에러:', error);
          const statusParam = filter !== 'ALL' ? `&status=${filter}` : '';
          apiClient.get<PageResponse>(
            `/images?page=${currentPage}&size=${pageSize}&sourceType=imageUrl${statusParam}`
          ).then(res => {
            const normalized = res.data.content.map(img => normalizeImageInfo(img));
            setImages(normalized);
            setTotalPages(res.data.totalPages);
            setTotalElements(res.data.totalElements ?? 0);

            const updated = normalized.find(img => img.id === resolvedId);
            if (updated) {
              setScanResult(updated);
              setRequestPhase(getPhaseByStatus(updated.status));
              if (updated.status === 'COMPLETED') {
                setError(null);
              }
            }
            setRegeneratingIds(prev => {
              const next = new Set(prev);
              next.delete(resolvedId);
              return next;
            });
          });
        }
      });
    } catch (err: any) {
      console.error('재분석 에러:', err);
      if (scanResult?.id === resolvedId || !scanResult?.id || (scanResult?.id as any) < 0) {
        setRequestPhase('FAILED');
        setScanResult(prev => (prev ? {
          ...prev,
          status: 'FAILED',
          altGenerationType: 'ERROR',
        } : prev));
      }
      
      // 로딩 상태 제거
      setRegeneratingIds(prev => {
        const next = new Set(prev);
        next.delete(resolvedId);
        return next;
      });
      
      const resData = err.response?.data;
      const errType = resData?.errorType;
      const errMsg = resData?.message;

      if (errType === 'SSRF_BLOCKED' || errType === 'NETWORK_TIMEOUT') {
        setError(buildImageAccessError(errType));
        fetchImages(currentPage, filter === 'ALL' ? null : filter);
        return;
      }

      // ✅ 이슈 #3: CDN_ACCESS_DENIED → 구조화 에러 UI + 업로드 유도 버튼
      if (errType === 'CDN_ACCESS_DENIED') {
        setError(buildImageAccessError('CDN_ACCESS_DENIED'));
        fetchImages(currentPage, filter === 'ALL' ? null : filter);
        return;
      }

      // ✅ 이슈 #2: QUOTA_EXCEEDED → 사용량 초과 메시지
      if (errType === 'QUOTA_EXCEEDED') {
        setError(errMsg || '이번 달 AI 사용 한도를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.');
        fetchAiUsage();
        fetchImages(currentPage, filter === 'ALL' ? null : filter);
        return;
      }

      let errorMessage = '재분석 중 오류가 발생했습니다.';

      if (err instanceof KakaoCDNBlockedError) {
        errorMessage = `⚠️ Kakao CDN 이미지 분석 제한\n\n${err.message}`;
      } else if (errMsg) {
        // ✅ 이슈 #2: B/E 표준화 완료 — message가 이미 한국어로 반환됨 (sanitize 불필요)
        errorMessage = errMsg;
      } else if (resData) {
        // 레거시 응답 처리
        const errorData = typeof resData === 'string' ? resData : JSON.stringify(resData);
        errorMessage = `❌ 재분석 오류\n\n${errorData.substring(0, 200)}`;
      } else if (err.code === 'ERR_NETWORK') {
        errorMessage = '❌ 서버 연결 오류\n\n백엔드 서버가 실행 중인지 확인해주세요.';
      }

      setError(errorMessage);
      alert(errorMessage);
      
      // 실패 상태 반영을 위해 새로고침
      fetchImages(currentPage, filter === 'ALL' ? null : filter);
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { className: string; icon: React.ElementType; label: string }> = {
      PENDING: { className: 'bg-gray-200 text-gray-800', icon: Clock, label: '대기중' },
      COMPLETED: { className: 'bg-green-200 text-green-800', icon: CheckCircle2, label: '완료' },
      FAILED: { className: 'bg-red-200 text-red-800', icon: AlertCircle, label: '오류' }
    };

    const item = config[status] ?? { className: 'bg-gray-200 text-gray-800', icon: Clock, label: status || '대기중' };
    const { className, icon: Icon, label } = item;

    return (
      <Badge className={`flex items-center gap-1 w-fit ${className}`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getAltTypeBadge = (type: AltGenerationType) => {
    const config: Record<string, { className: string; icon: React.ElementType; label: string }> = {
      AI: { className: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2, label: '✓ AI 분석' },
      FALLBACK: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: AlertCircle, label: '⚠ 임시 생성' },
      ERROR: { className: 'bg-red-100 text-red-800 border-red-300', icon: AlertCircle, label: '✗ 생성 오류' },
      PENDING: { className: hasPendingItems ? 'bg-blue-100 text-blue-800 border-blue-300 animate-pulse' : 'bg-gray-100 text-gray-800 border-gray-300', icon: hasPendingItems ? Loader2 : Clock, label: hasPendingItems ? '⏳ AI 생성중' : '📝 미생성' },
      SKIPPED_DECORATIVE: { className: 'bg-purple-100 text-purple-800 border-purple-300', icon: AlertCircle, label: '🎨 장식용 이미지' }
    };

    const item = config[type] ?? { className: 'bg-gray-100 text-gray-800 border-gray-300', icon: Clock, label: '미생성' };
    const { className, icon: Icon, label } = item;

    return (
      <Badge className={`flex items-center gap-1 w-fit border ${className}`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getAltTypeStats = () => {
    if (!allStats) {
      return { ai: 0, fallback: 0, pending: 0, error: 0, skippedDecorative: 0, total: 0, aiPercentage: 0 };
    }
    return allStats;
  };

  const filteredImages = images;

  return (
    <>
      <PageTitle title="이미지 URL ALT 생성" />
      <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || '이미지 URL 입력'}
        description="개별 이미지 URL을 입력하면 생성 요청을 접수하고 상태를 자동으로 동기화합니다"
        icon={menuIcon}
        action={
          <Button variant="outline" onClick={() => setShowGuide(!showGuide)} className="shrink-0">
            <HelpCircle className="w-4 h-4 mr-2" />
            사용 가이드
          </Button>
        }
      />
      <div className="space-y-8">
        <Card>
          <CardContent className="space-y-6 pt-6">
            {/* AI 사용량 표시 */}
            {aiUsage && (
              <div className={`flex flex-wrap items-center justify-between gap-2 px-4 py-2 rounded-lg text-sm border ${
                aiUsage.isTeamContext
                  ? (aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null && aiUsage.teamTotalUsed >= aiUsage.teamLimit)
                    ? 'bg-red-50 border-red-300'
                    : 'bg-blue-50 border-blue-200'
                  : (!aiUsage.canUse ? 'bg-red-50 border-red-300' :
                  (aiUsage.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))
                    ? 'bg-orange-50 border-orange-300'
                    : 'bg-gray-50 border-gray-200')
              }`}>
                <span className="text-gray-700">
                  {aiUsage.isTeamContext ? (
                    <>
                      {aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null ? (
                        <>
                          팀 사용량: <strong>{aiUsage.teamTotalUsed.toLocaleString()}</strong> / {aiUsage.teamLimit.toLocaleString()}건
                          <span className="ml-2 text-gray-500">(본인: {aiUsage.used.toLocaleString()}건)</span>
                        </>
                      ) : (
                        <>
                          본인 사용량: <strong>{aiUsage.used.toLocaleString()}</strong> / {aiUsage.limit.toLocaleString()}건
                        </>
                      )}
                      <span className="ml-2 text-gray-500">({aiUsage.teamName} 팀, {aiUsage.role === 'OWNER' ? '팀장' : '팀원'}, {aiUsage.plan})</span>
                    </>
                  ) : (
                    <>
                      이번 달 AI 사용량: <strong>{aiUsage.used.toLocaleString()}</strong> / {aiUsage.limit.toLocaleString()}건
                      <span className="ml-2 text-gray-500">({aiUsage.plan} 플랜입니다)</span>
                    </>
                  )}
                </span>
                {aiUsage.isTeamContext ? (
                  aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null && aiUsage.teamTotalUsed >= aiUsage.teamLimit && (
                    <span className="text-red-600 font-semibold text-xs">⚠️ 팀 한도 초과 &mdash; 관리자에게 문의해 주세요.</span>
                  )
                ) : (
                  !aiUsage.canUse && (
                    <span className="text-red-600 font-semibold text-xs">⚠️ 한도 초과 &mdash; 관리자에게 문의해 주세요.</span>
                  )
                )}
                {!aiUsage.isTeamContext && aiUsage.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (
                  (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0) ? (
                    <span className="text-orange-600 font-semibold text-xs">
                      ⚠️ 오늘 {(aiUsage.dailyUsed ?? 0)}/{aiUsage.dailyLimit}건 &mdash; 일일 한도 초과. 내일 다시 이용하거나 플랜을 업그레이드하세요.
                    </span>
                  ) : (
                    <span className="text-gray-500 text-xs">
                      오늘 {(aiUsage.dailyUsed ?? 0)}/{aiUsage.dailyLimit}건 (무료 플랜 일일 한도)
                    </span>
                  )
                )}
              </div>
            )}

            {/* 사용 가이드 */}
            {showGuide && (
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-900">
                    <Info className="w-5 h-5" />
                    이미지 URL 입력 사용 가이드
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">📌 기능 개요</h4>
                    <p className="text-gray-700">
                      웹상의 이미지 URL을 직접 입력하여 <strong>ALT 생성 요청을 접수</strong>하고,
                      처리 상태(PENDING/COMPLETED/FAILED)를 자동으로 동기화하는 기능입니다.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">🎯 사용 방법</h4>
                    <ol className="list-decimal pl-5 space-y-1 text-gray-700">
                      <li>이미지 URL 입력칸에 이미지 주소 입력</li>
                      <li>"ALT 생성" 버튼 클릭</li>
                      <li>요청 접수 후 처리 상태가 자동 갱신됨</li>
                      <li>완료 시 생성된 ALT 텍스트 확인 및 복사</li>
                      <li>필요시 "재생성" 버튼으로 다시 생성</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <FileCode className="w-4 h-4" />
                      사용 가능한 URL 형식
                    </h4>
                    <div className="bg-gray-900 rounded-lg p-3">
                      <pre className="text-green-400 text-xs space-y-1">
{`https://example.com/image.jpg
https://example.com/images/photo.png
https://cdn.example.com/assets/pic.webp
https://example.com/img?id=123&size=large`}
                      </pre>
                    </div>
                    <p className="text-gray-600 text-xs mt-2">
                      ✓ 지원 형식: JPG, PNG, WEBP, GIF 등 대부분의 이미지 형식
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">💡 팁</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li>이미지 URL은 <strong>직접 접근 가능한 공개 URL</strong>이어야 합니다</li>
                      <li>로그인이 필요한 사이트의 이미지는 업로드 기능을 사용하세요</li>
                      <li>ALT 텍스트를 더블클릭하면 바로 복사됩니다</li>
                      <li>HTML 태그 버튼으로 전체 img 태그를 복사할 수 있습니다</li>
                      <li>이력 탭에서 이전에 처리한 이미지를 확인할 수 있습니다</li>
                      <li><strong>캐시 시스템</strong>: 동일 이미지는 캐시에서 빠르게 제공됩니다 (캐시 횟수 표시)</li>
                      <li><strong>강제 재분석</strong>: 캐시가 있는 경우 "강제 재분석" 버튼으로 새로 생성 가능 (AI 사용량 차감)</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">🔄 ALT 생성 타입</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li><Badge className="bg-green-600 text-white text-xs">AI</Badge> OpenAI로 생성된 고품질 ALT 텍스트</li>
                      <li><Badge className="bg-yellow-600 text-white text-xs">FALLBACK</Badge> Google Vision 등 대체 API로 생성</li>
                      <li><Badge className="bg-gray-600 text-white text-xs">PENDING</Badge> 아직 생성되지 않음</li>
                      <li><Badge className="bg-red-600 text-white text-xs">ERROR</Badge> 생성 오류</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">⭐ 언제 사용하나요?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li><strong>블로그/커뮤니티</strong>: 사용자가 업로드한 이미지 URL에 ALT 추가</li>
                      <li><strong>CMS/에디터</strong>: 콘텐츠 편집 중 외부 이미지에 ALT 추가</li>
                      <li><strong>빠른 테스트</strong>: 1개 이미지만 빠르게 처리하고 싶을 때</li>
                      <li><strong>API 테스트</strong>: ALT 생성 품질 테스트용</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <p className="text-yellow-900 text-xs">
                      <strong>⚠️ 주의:</strong> CORS 정책으로 인해 일부 사이트의 이미지는 접근이 차단될 수 있습니다. 
                      이 경우 이미지 업로드 기능을 사용하세요.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {isSuperAdmin && (
              <Alert className="bg-amber-50 border-amber-300">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  SUPER_ADMIN 계정은 ALT 생성 기능을 사용할 수 없습니다. ALT 생성은 CLIENT 계정으로 로그인하여 이용해 주세요.
                </AlertDescription>
              </Alert>
            )}

            {error && (
              typeof error === 'string' ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : (
                // 구조화된 에러 응답 UI
                <Alert variant="destructive" className="flex gap-3 items-start">
                  <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{error.message}</p>
                    <p className="text-sm mt-1 whitespace-pre-line leading-relaxed">
                      {(() => {
                        const isActuallyMissingImage =
                          error.errorType === 'CDN_ACCESS_DENIED' &&
                          !!scanResult &&
                          browserFailedImages.has(scanResult.id);
                        const resolvedErrorDetail = isActuallyMissingImage
                          ? '이미지를 불러올 수 없습니다.'
                          : error.errorDetail;
                        return resolvedErrorDetail?.includes('이미지 업로드')
                          ? resolvedErrorDetail.split('이미지 업로드').map((part, i, arr) =>
                            i < arr.length - 1
                              ? <React.Fragment key={i}>{part}<span
                                  className="underline cursor-pointer hover:opacity-70 font-semibold"
                                  onClick={() => navigate('/dashboard/crawler/upload')}
                                  title="이미지 업로드 페이지로 이동"
                                >이미지 업로드</span></React.Fragment>
                              : <React.Fragment key={i}>{part}</React.Fragment>
                          )
                          : resolvedErrorDetail;
                      })()}
                    </p>
                    {error.actionRequired && error.actionUrl && !(error.errorType === 'CDN_ACCESS_DENIED' && !!scanResult && browserFailedImages.has(scanResult.id)) && (
                      <div className="mt-3 pt-3 border-t border-red-200">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => navigate(error.actionUrl!)}
                        >
                          📁 {error.actionText}
                        </Button>
                        {error.actionDescription && (
                          <p className="text-xs text-gray-500 mt-1.5">{error.actionDescription}</p>
                        )}
                      </div>
                    )}
                  </div>
                </Alert>
              )
            )}

            {duplicateNotice && (
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">{duplicateNotice}</AlertDescription>
              </Alert>
            )}

            {/* 컨텍스트 프로필 옵션 */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="useContextProfile"
                checked={useContextProfile}
                onChange={(e) => setUseContextProfile(e.target.checked)}
                disabled={!profileActive}
                className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
              />
              <label
                htmlFor="useContextProfile"
                className={`text-sm select-none ${profileActive ? 'cursor-pointer' : 'cursor-not-allowed text-gray-400'}`}
              >
                개인별 컨텍스트 프로필 적용
                {profileActive
                  ? <span className="text-xs text-gray-500 ml-1">(설정 → 컨텍스트 프로필에서 업종/사이트 유형 등 설정 가능)</span>
                  : <span className="text-xs text-amber-600 ml-1">(컨텍스트 프로필을 먼저 등록해주세요 → 프로필 메뉴)</span>
                }
              </label>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <Input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                disabled={processing}
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleProcessImage();
                  }
                }}
              />
              <Button
                onClick={handleProcessImage}
                disabled={processing || !imageUrl.trim() || isSuperAdmin}
                className="w-full md:w-auto"
              >
                {processing ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    URL 분석 중...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    ALT 생성
                  </>
                )}
              </Button>
            </div>

            {imageUrl.trim() && !scanResult && (
              <Card className="border-dashed border-gray-300 bg-white/70">
                <CardContent className="pt-4">
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <img
                      src={imageUrl.trim()}
                      alt="입력한 이미지 미리보기"
                      className="w-full sm:w-32 h-32 object-cover rounded-lg bg-gray-100"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="space-y-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">입력한 이미지 미리보기</p>
                      <p className="text-xs text-gray-500 break-all">{imageUrl.trim()}</p>
                      <p className="text-xs text-gray-500">미리보기는 바로 표시되고, Base64 변환은 최대 2초까지만 시도합니다.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {scanResult && (
              <Card className={scanResult.status === 'PENDING' ? 'bg-blue-50 border-blue-200' : scanResult.status === 'FAILED' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {scanResult.status === 'PENDING' && (
                      <Spinner size="sm" className="text-blue-500" />
                    )}
                    처리 결과
                  </CardTitle>
                  {requestPhase !== 'IDLE' && (
                    <p className={`text-xs rounded px-2 py-1 mt-1 w-fit ${
                      requestPhase === 'COMPLETED'
                        ? 'text-green-700 bg-green-100'
                        : requestPhase === 'FAILED'
                          ? 'text-red-700 bg-red-100'
                          : requestPhase === 'TIMEOUT'
                            ? 'text-amber-700 bg-amber-100'
                            : 'text-blue-700 bg-blue-100'
                    }`}>
                      {REQUEST_PHASE_TEXT[requestPhase]}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {scanResult.status === 'PENDING' ? (
                      requestPhase === 'TIMEOUT' ? (
                        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 space-y-2">
                          <p className="text-sm text-amber-800 font-medium">⚠️ AI 분석이 자동으로 완료되지 않았습니다.</p>
                          <p className="text-xs text-amber-700">{resolveDisplayImageUrlText(scanResult)}</p>
                          <p className="text-xs text-amber-700">아래 버튼을 눌러 직접 ALT를 생성해 주세요.</p>
                          <Button
                            onClick={() => handleRegenerateAlt(scanResult.id)}
                            size="sm"
                            variant="outline"
                            className="mt-1 text-amber-700 border-amber-400 hover:bg-amber-100 whitespace-nowrap"
                            disabled={regeneratingIds.has(scanResult.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                          >
                            {regeneratingIds.has(scanResult.id)
                              ? <><Spinner size="xs" className="mr-1" />분석중...</>
                              : <>🔄 ALT 생성</>}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 gap-3 text-blue-600">
                          <Spinner size="2xl" />
                          <p className="text-sm font-medium">AI가 이미지를 분석하고 있습니다...</p>
                          <p className="text-xs text-gray-500 break-all">{resolveDisplayImageUrlText(scanResult)}</p>
                        </div>
                      )
                    ) : (
                    <div className="flex flex-col md:flex-row gap-4">
                      <img
                        src={resolveRenderableImageSrc(scanResult)}
                        alt={scanResult.generatedAlt || ''}
                        className="w-full md:w-48 h-48 object-cover rounded-lg"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="192" height="192"%3E%3Crect fill="%23ddd" width="192" height="192"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                          // 브라우저에서도 로드 실패 → 진짜 없는 이미지
                          setBrowserFailedImages(prev => new Set(Array.from(prev).concat(scanResult.id)));
                        }}
                      />
                      <div className="flex-1 space-y-3">
                        <div className="flex gap-2 flex-wrap">
                          {getStatusBadge(scanResult.status)}
                          {getAltTypeBadge(scanResult.altGenerationType)}
                          {scanResult.cachecnt !== undefined && (
                            <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-xs">
                              캐시 {scanResult.cachecnt}회
                            </Badge>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-700">이미지 URL:</p>
                          <p className="text-sm text-gray-600 break-all">{resolveDisplayImageUrlText(scanResult)}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            최종 수정: {formatRelativeTime(getImageActivityAt(scanResult))} ({formatDateTime(getImageActivityAt(scanResult))})
                          </p>
                        </div>
                        {scanResult.status === 'FAILED' && (
                          <div className="flex flex-wrap gap-2 mt-1">
                            <Button
                              onClick={() => handleRegenerateAlt(scanResult.id)}
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50 whitespace-nowrap"
                              disabled={regeneratingIds.has(scanResult.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                            >
                              {regeneratingIds.has(scanResult.id)
                                ? <><Spinner size="xs" className="mr-1" />분析중...</>
                                : <>🔄 재시도</>}
                            </Button>
                          </div>
                        )}
                        {scanResult.generatedAlt && (
                          <Card className={`${
                            scanResult.altGenerationType === 'AI' ? 'bg-green-50 border-green-200' :
                            scanResult.altGenerationType === 'FALLBACK' ? 'bg-yellow-50 border-yellow-200' :
                            'bg-gray-50 border-gray-200'
                          }`}>
                            <CardContent className="p-3">
                              <div className="flex flex-col sm:flex-row gap-2">
                                <div className="flex-1 min-w-0">
                                  <p 
                                    className={`text-sm mb-1 p-2 rounded transition-colors ${
                                      scanResult.altGenerationType === 'AI' && scanResult.status === 'COMPLETED' ? 'font-semibold' : ''
                                    } ${
                                      scanResult.altGenerationType === 'AI' ? 'text-green-800' :
                                      scanResult.altGenerationType === 'FALLBACK' ? 'text-yellow-800' :
                                      scanResult.altGenerationType === 'ERROR' ? 'text-red-600' :
                                      'text-gray-800'
                                    } ${
                                      scanResult.altGenerationType !== 'ERROR' ? 'cursor-pointer hover:bg-white/50' : ''
                                    }`}
                                    {...(scanResult.altGenerationType !== 'ERROR' && {
                                      onDoubleClick: () => handleDoubleClick(scanResult.generatedAlt!),
                                      title: '더블클릭하여 복사'
                                    })}
                                  >
                                    {scanResult.altGenerationType === 'ERROR'
                                      ? (() => {
                                          const errType = browserFailedImages.has(scanResult.id)
                                            ? 'IMAGE_NOT_FOUND'
                                            : classifyImageError(scanResult.generatedAlt, scanResult.imageSrc);
                                          if (errType === 'CDN_BLOCKED') {
                                            return (
                                              <span className="block space-y-1">
                                                <span className="block font-semibold">🔒 AI 서버의 이미지 접근이 차단되었습니다</span>
                                                <span className="block text-xs text-red-700 leading-relaxed">
                                                  이 이미지는 <strong>화면에서는 정상적으로 보이지만</strong>, Kakao CDN의 보안 정책(Signed URL)에 의해 AI 분석 서버에서의 직접 접근이 차단됩니다.
                                                </span>
                                                <span className="block text-xs text-gray-500 leading-relaxed">
                                                  💡 <strong>왜 내 PC에서는 보이나요?</strong> 브라우저는 원본 블로그 페이지를 통해 이미지를 로드하기 때문에 허용되지만, AI 서버는 외부에서 직접 요청하므로 CDN이 차단합니다. 원본 사이트가 아닌 곳에서의 접근은 CDN 보안 정책상 일괄 거부됩니다.
                                                </span>
                                                <span className="block text-xs text-orange-700 font-medium">
                                                  → 이미지를 PC에 저장 후{' '}
                                                  <span
                                                    className="underline cursor-pointer hover:text-red-800 font-semibold"
                                                    onClick={() => navigate('/dashboard/crawler/upload')}
                                                    title="이미지 업로드 페이지로 이동"
                                                  >이미지 업로드</span>{' '}메뉴에서 직접 업로드하면 분석할 수 있습니다.
                                                </span>
                                              </span>
                                            );
                                          }
                                          if (errType === 'IMAGE_NOT_FOUND') {
                                            return <span>이미지를 불러올 수 없습니다.</span>;
                                          }
                                          return scanResult.generatedAlt;
                                        })()
                                      : scanResult.generatedAlt
                                    }
                                  </p>
                                  {scanResult.altGenerationType === 'FALLBACK' && (
                                    <p className="text-xs text-yellow-700 mt-2 italic">
                                      💡 이 설명은 URL 기반으로 임시 생성되었습니다. OpenAI API 키를 설정하면 더 정확한 분석을 받을 수 있습니다.
                                    </p>
                                  )}
                                </div>
                                {scanResult.altGenerationType !== 'ERROR' && (
                                  <div className="flex flex-wrap gap-2 shrink-0">
                                    <Button
                                      onClick={() => handleCopyAlt(scanResult.generatedAlt!, scanResult.id)}
                                      size="sm"
                                      variant="outline"
                                      className="whitespace-nowrap"
                                    >
                                      {copiedId === scanResult.id ? (
                                        <>
                                          <Check className="w-3 h-3 mr-1 text-green-600" />
                                          복사됨
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="w-3 h-3 mr-1" />
                                          ALT 복사
                                        </>
                                      )}
                                    </Button>
                                    <Button
                                      onClick={() => handleCopyHtml(scanResult as ImageInfo)}
                                      size="sm"
                                      variant="outline"
                                      className="whitespace-nowrap"
                                    >
                                      {copiedHtmlId === scanResult.id ? (
                                        <>
                                          <Check className="w-3 h-3 mr-1 text-green-600" />
                                          복사됨
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="w-3 h-3 mr-1" />
                                          HTML 복사
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>

        {(() => {
          const stats = getAltTypeStats();
          // pending 수 변화 감지 → 0이 되면 완료 배너
          if (prevPendingCountRef.current > 0 && stats.pending === 0 && !allCompletedBanner) {
            setAllCompletedBanner(true);
            setTimeout(() => setAllCompletedBanner(false), 8000);
          }
          prevPendingCountRef.current = stats.pending;
          return (
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardHeader>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                  <CardTitle className="text-lg">ALT 텍스트 생성 통계
                    <span className="text-sm font-normal text-gray-500 ml-2">— 조회 결과 기준</span>
                  </CardTitle>
                  {hasPendingItems && (
                    <p className="text-sm text-blue-600 flex items-center gap-1">
                      <Spinner size="3.5" />
                      AI가 ALT 텍스트를 생성하고 있습니다 &mdash; 잠시 기다려 주세요
                    </p>
                  )}
                  {allCompletedBanner && !hasPendingItems && (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      모든 이미지의 ALT 텍스트 생성이 완료되었습니다!
                    </p>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-green-600">{stats.ai}</p>
                    <p className="text-xs text-gray-600 mt-1">AI 분석</p>
                    <p className="text-xs text-green-600 font-semibold">{stats.aiPercentage}% 정확도</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-yellow-600">{stats.fallback}</p>
                    <p className="text-xs text-gray-600 mt-1">임시 생성</p>
                    <p className="text-xs text-yellow-600 font-semibold">재분석 권장</p>
                  </div>
                  <div className={`text-center p-4 rounded-lg shadow-sm transition-colors ${
                    hasPendingItems ? 'bg-blue-50 ring-2 ring-blue-300' :
                    allCompletedBanner && stats.pending === 0 ? 'bg-green-50' : 'bg-white'
                  }`}>
                    <p className={`text-3xl font-bold transition-all ${
                      hasPendingItems ? 'text-blue-600' :
                      allCompletedBanner && stats.pending === 0 ? 'text-green-600' : 'text-gray-600'
                    }`}>
                      {allCompletedBanner && stats.pending === 0 ? '✅' : stats.pending}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {hasPendingItems
                        ? <span className="text-blue-600 font-semibold flex items-center justify-center gap-1"><Spinner size="xs" />생성중...</span>
                        : allCompletedBanner && stats.pending === 0
                          ? <span className="text-green-600 font-semibold">완료!</span>
                          : '미생성'
                      }
                    </p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-red-600">{stats.error}</p>
                    <p className="text-xs text-gray-600 mt-1">오류</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-purple-600">{stats.skippedDecorative}</p>
                    <p className="text-xs text-gray-600 mt-1">장식용 이미지</p>
                    <p className="text-xs text-purple-600 font-semibold">ALT 생성 건너뜀</p>
                  </div>
                </div>
                {stats.fallback > stats.ai && stats.fallback > 0 && (
                  <Alert className="mt-4 bg-yellow-50 border-yellow-300">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-800">
                      <strong>⚠️ 임시 생성된 ALT 텍스트가 많습니다.</strong>
                      <br />
                      OpenAI API 키를 설정하면 더 정확한 이미지 분석을 받을 수 있습니다.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          );
        })()}

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <CardTitle className="text-xl">이미지 목록({totalElements.toLocaleString()}건)</CardTitle>
              <div className="flex gap-2">
                {images.length > 0 && (
                  <Button
                    onClick={handleExcelDownload}
                    disabled={excelLoading}
                    variant="outline"
                    size="sm"
                    className="text-green-700 border-green-400 hover:bg-green-50"
                  >
                    {excelLoading ? (
                      <><Spinner size="sm" className="mr-2" />다운로드 중...</>
                    ) : (
                      <><FileDown className="w-4 h-4 mr-2" />엑셀 다운로드 ({totalElements.toLocaleString()}건)</>
                    )}
                  </Button>
                )}
                {images.length > 0 && (
                  <Button
                    onClick={handleClearAll}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    전체 삭제
                  </Button>
                )}
                  <Button
                    onClick={() => { setAltTypeFilter('ALL'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'ALL'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'ALL' ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600' : ''}
                  >
                    전체
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('AI'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'AI'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'AI' ? 'bg-green-600 text-white hover:bg-green-700 border-green-600' : ''}
                  >
                    ✓ AI 분석
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('FALLBACK'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'FALLBACK'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'FALLBACK' ? 'bg-yellow-600 text-white hover:bg-yellow-700 border-yellow-600' : ''}
                  >
                    ⚠ 임시 생성
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('PENDING'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'PENDING'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'PENDING' ? 'bg-gray-600 text-white hover:bg-gray-700 border-gray-600' : ''}
                  >
                    📝 미생성
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('ERROR'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'ERROR'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'ERROR' ? 'bg-red-600 text-white hover:bg-red-700 border-red-600' : ''}
                  >
                    ✗ 오류
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('SKIPPED_DECORATIVE'); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, 'SKIPPED_DECORATIVE'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'SKIPPED_DECORATIVE' ? 'bg-purple-600 text-white hover:bg-purple-700 border-purple-600' : ''}
                  >
                    🎨 장식용
                  </Button>
                </div>
              </div>
          </CardHeader>
          <CardContent>
            <div>

              <Tabs value={filter} onValueChange={(v) => {
                const newFilter = v as 'ALL' | 'PENDING' | 'COMPLETED';
                setFilter(newFilter);
                fetchImages(0, newFilter === 'ALL' ? null : newFilter, pageSize, undefined, undefined, undefined, startDate, endDate);
              }} className="w-full">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                  <TabsList className="grid grid-cols-3 flex-1">
                    <TabsTrigger value="ALL">전체</TabsTrigger>
                    <TabsTrigger value="PENDING">대기</TabsTrigger>
                    <TabsTrigger value="COMPLETED">완료</TabsTrigger>
                  </TabsList>
                  <div className="flex items-center gap-1 shrink-0">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, undefined, e.target.value, endDate); }}
                        className="h-9 w-full md:w-auto text-xs"
                      />
                      <span className="text-gray-500 text-xs">~</span>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => { setEndDate(e.target.value); fetchImages(0, filter === 'ALL' ? null : filter, pageSize, undefined, undefined, undefined, startDate, e.target.value); }}
                        className="h-9 w-full md:w-auto text-xs"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Select
                      value={`${sortBy}_${sortDir}`}
                      onValueChange={(v) => {
                        const lastIdx = v.lastIndexOf('_');
                        const newSortBy = v.substring(0, lastIdx) as SortBy;
                        const newSortDir = v.substring(lastIdx + 1) as SortDir;
                        setSortBy(newSortBy);
                        setSortDir(newSortDir);
                        fetchImages(0, filter === 'ALL' ? null : filter, pageSize, newSortBy, newSortDir, undefined, startDate, endDate);
                      }}
                    >
                      <SelectTrigger className="w-[180px] h-9 text-sm">
                        <SelectValue placeholder="정렬" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UPDATED_AT_DESC">수정일 최신순{sortIsDefault && sortBy === 'UPDATED_AT' && sortDir === 'DESC' ? ' (기본)' : ''}</SelectItem>
                        <SelectItem value="UPDATED_AT_ASC">수정일 오래된순</SelectItem>
                        <SelectItem value="CREATED_AT_DESC">등록일 최신순</SelectItem>
                        <SelectItem value="CREATED_AT_ASC">등록일 오래된순</SelectItem>
                        <SelectItem value="STATUS_ASC">상태순</SelectItem>
                        <SelectItem value="URL_NORMALIZED_ASC">URL 오름차순</SelectItem>
                        <SelectItem value="URL_NORMALIZED_DESC">URL 내림차순</SelectItem>
                        <SelectItem value="COMPLETION_DESC">완료율 높은순</SelectItem>
                        <SelectItem value="COMPLETION_ASC">완료율 낮은순</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <TabsContent value={filter} className="mt-6">
                  {filteredImages.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>{images.length === 0 ? '아직 처리된 이미지가 없습니다' : '해당 조건의 이미지가 없습니다'}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredImages.map((img) => (
                        <Card
                          key={img.id}
                          className={`hover:shadow-md transition-shadow ${highlightedImageId === img.id ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex flex-col md:flex-row gap-4">
                              <img
                                src={resolveRenderableImageSrc(img)}
                                alt={img.generatedAlt || ''}
                                title={(img.status === 'COMPLETED' && img.altGenerationType === 'AI') ? img.generatedAlt || '' : undefined}
                                className="w-full md:w-32 h-32 object-cover rounded-lg"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="128" height="128"%3E%3Crect fill="%23ddd" width="128" height="128"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                  // 브라우저에서도 로드 실패 → 진짜 없는 이미지
                                  setBrowserFailedImages(prev => new Set(Array.from(prev).concat(img.id)));
                                }}
                              />
                              <div className="flex-1 space-y-2">
                                <div className="flex gap-2 flex-wrap">
                                  {getStatusBadge(img.status)}
                                  {getAltTypeBadge(img.altGenerationType)}
                                  {img.cachecnt !== undefined && (
                                    <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-xs">
                                      캐시 {img.cachecnt}회
                                    </Badge>
                                  )}
                                  {img.altApprovalStatus === 'PENDING' && (
                                    <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200 text-xs">⏳ 검수대기</Badge>
                                  )}
                                  {img.altApprovalStatus === 'APPROVED' && (
                                    <Badge className="bg-green-100 text-green-700 border border-green-200 text-xs">✅ 승인됨</Badge>
                                  )}
                                  {img.altApprovalStatus === 'REJECTED' && (
                                    <Badge className="bg-red-100 text-red-700 border border-red-200 text-xs">❌ 거부됨</Badge>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm text-gray-600 break-all">
                                    <span className="font-semibold">이미지 URL:</span> {resolveDisplayImageUrlText(img)}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    최종 수정: {formatRelativeTime(getImageActivityAt(img))} ({formatDateTime(getImageActivityAt(img))})
                                  </p>
                                </div>
                                {img.status === 'PENDING' ? (
                                  isLongPending(img.createdAt) ? (
                                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                                      <p className="text-sm text-amber-800 font-medium">⚠️ AI 분석이 자동으로 완료되지 않았습니다.</p>
                                      <p className="text-xs text-amber-700 mt-1">아래 버튼을 눌러 직접 ALT를 생성해 주세요.</p>
                                      <Button
                                        onClick={() => handleRegenerateAlt(img.id)}
                                        size="sm"
                                        variant="outline"
                                        className="mt-2 text-amber-700 border-amber-400 hover:bg-amber-100 whitespace-nowrap"
                                        disabled={regeneratingIds.has(img.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                                      >
                                        {regeneratingIds.has(img.id)
                                          ? <><Spinner size="xs" className="mr-1" />분석중...</>
                                          : <>🔄 ALT 생성</>}
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                                      <p className="text-sm text-blue-800 font-medium">⏳ AI가 ALT 텍스트를 분석 중입니다.</p>
                                      <p className="text-xs text-blue-600 mt-1">잠시 후 자동으로 결과가 표시됩니다.</p>
                                    </div>
                                  )
                                ) : img.generatedAlt ? (
                                  <Card className={`${
                                    img.altGenerationType === 'AI' ? 'bg-green-50 border-green-200' :
                                    img.altGenerationType === 'FALLBACK' ? 'bg-yellow-50 border-yellow-200' :
                                    'bg-gray-50 border-gray-200'
                                  }`}>
                                    <CardContent className="p-3">
                                      <div className="flex flex-col sm:flex-row gap-2">
                                        <div className="flex-1 min-w-0">
                                          <p 
                                            className={`text-sm mb-1 p-2 rounded transition-colors ${
                                              img.altGenerationType === 'AI' && img.status === 'COMPLETED' ? 'font-semibold' : ''
                                            } ${
                                              img.altGenerationType === 'AI' ? 'text-green-800' :
                                              img.altGenerationType === 'FALLBACK' ? 'text-yellow-800' :
                                              img.altGenerationType === 'ERROR' ? 'text-red-600' :
                                              'text-gray-800'
                                            } ${
                                              img.altGenerationType !== 'ERROR' ? 'cursor-pointer hover:bg-white/50' : ''
                                            }`}
                                            {...(img.altGenerationType !== 'ERROR' && {
                                              onDoubleClick: () => handleDoubleClick(img.generatedAlt!),
                                              title: '더블클릭하여 복사'
                                            })}
                                          >
                                            {img.altGenerationType === 'ERROR'
                                              ? (() => {
                                                  // 브라우저에서도 이미지 로드 실패하면 → 실제로 없는 이미지
                                                  const errType = browserFailedImages.has(img.id)
                                                    ? 'IMAGE_NOT_FOUND'
                                                    : classifyImageError(img.generatedAlt, img.imageSrc);
                                                  if (errType === 'CDN_BLOCKED') {
                                                    return (
                                                      <span className="block space-y-1">
                                                        <span className="block font-semibold">🔒 AI 서버의 이미지 접근이 차단되었습니다</span>
                                                        <span className="block text-xs text-red-700 leading-relaxed">
                                                          이 이미지는 <strong>화면에서는 정상적으로 보이지만</strong>, Kakao CDN의 보안 정책(Signed URL)에 의해 AI 분석 서버에서의 직접 접근이 차단됩니다.
                                                        </span>
                                                        <span className="block text-xs text-gray-500 leading-relaxed">
                                                          💡 <strong>왜 내 PC에서는 보이나요?</strong> 브라우저는 원본 블로그 페이지를 통해 이미지를 로드하기 때문에 허용되지만, AI 서버는 외부에서 직접 요청하므로 CDN이 차단합니다. 원본 사이트가 아닌 곳에서의 접근은 CDN 보안 정책상 일괄 거부됩니다.
                                                        </span>
                                                        <span className="block text-xs text-orange-700 font-medium">
                                                          → 이미지를 PC에 저장 후{' '}
                                                          <span
                                                            className="underline cursor-pointer hover:text-red-800 font-semibold"
                                                            onClick={() => navigate('/dashboard/crawler/upload')}
                                                            title="이미지 업로드 페이지로 이동"
                                                          >이미지 업로드</span>{' '}메뉴에서 직접 업로드하면 분석할 수 있습니다.
                                                        </span>
                                                      </span>
                                                    );
                                                  }
                                                  if (errType === 'IMAGE_NOT_FOUND') {
                                                    return <span>이미지를 불러올 수 없습니다.</span>;
                                                  }
                                                  // OTHER: 원본 메시지 표시 (이미지 업로드 링크 처리 포함)
                                                  return img.generatedAlt?.includes('이미지 업로드')
                                                    ? img.generatedAlt!.split('이미지 업로드').map((part, i, arr) =>
                                                        i < arr.length - 1
                                                          ? <React.Fragment key={i}>{part}<span
                                                              className="underline cursor-pointer hover:text-red-800 font-semibold"
                                                              onClick={() => navigate('/dashboard/crawler/upload')}
                                                              title="이미지 업로드 페이지로 이동"
                                                            >이미지 업로드</span></React.Fragment>
                                                          : <React.Fragment key={i}>{part}</React.Fragment>
                                                      )
                                                    : img.generatedAlt;
                                                })()
                                              : img.generatedAlt
                                            }
                                          </p>
                                          {img.altGenerationType === 'FALLBACK' && (
                                            <p className="text-xs text-yellow-700 mt-2 italic">
                                              💡 이 설명은 URL 기반으로 임시 생성되었습니다. 정확하지 않을 수 있습니다.
                                            </p>
                                          )}
                                        </div>
                                        {/* 커스텀 ALT 섹션 */}
                                        <div className="mt-4">
                                          <p className="text-xs font-semibold text-gray-500 mb-2">✏️ 커스텀 ALT</p>
                                          <CustomAltEditor
                                            imageId={img.id}
                                            customAltText={img.customAltText}
                                            onSaved={(customAltText) => handleCustomAltSaved(img.id, customAltText)}
                                            onDeleted={() => handleCustomAltDeleted(img.id)}
                                          />
                                        </div>
                                        <div className="flex flex-wrap gap-2 shrink-0">
                                          {(img.altGenerationType !== 'FALLBACK' && img.altGenerationType !== 'ERROR') && (
                                            <div className="flex flex-wrap gap-2">
                                              <Button
                                                onClick={() => handleCopyAlt(img.generatedAlt!, img.id)}
                                                size="sm"
                                                variant="outline"
                                                className="whitespace-nowrap"
                                              >
                                                {copiedId === img.id ? (
                                                  <>
                                                    <Check className="w-3 h-3 mr-1 text-green-600" />
                                                    복사됨
                                                  </>
                                                ) : (
                                                  <>
                                                    <Copy className="w-3 h-3 mr-1" />
                                                    ALT 복사
                                                  </>
                                                )}
                                              </Button>
                                              <Button
                                                onClick={() => handleCopyHtml(img)}
                                                size="sm"
                                                variant="outline"
                                                className="whitespace-nowrap"
                                              >
                                                {copiedHtmlId === img.id ? (
                                                  <>
                                                    <Check className="w-3 h-3 mr-1 text-green-600" />
                                                    복사됨
                                                  </>
                                                ) : (
                                                  <>
                                                    <Copy className="w-3 h-3 mr-1" />
                                                    HTML 복사
                                                  </>
                                                )}
                                              </Button>
                                            </div>
                                          )}
                                          {img.cachecnt !== undefined && img.cachecnt > 0 && (
                                            <Button
                                              onClick={() => handleRegenerateAlt(img.id, true)}
                                              variant="outline"
                                              size="sm"
                                              className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                              disabled={regeneratingIds.has(img.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                                            >
                                              {regeneratingIds.has(img.id) ? (
                                                <>
                                                  <Spinner size="sm" className="mr-1" />
                                                  강제 재분석 중...
                                                </>
                                              ) : (
                                                <>🔄 강제 재분석</>
                                              )}
                                            </Button>
                                          )}
                                          {img.status !== 'COMPLETED' && (!img.cachecnt || img.cachecnt === 0) && (
                                            <Button
                                              onClick={() => handleRegenerateAlt(img.id)}
                                              variant="outline"
                                              size="sm"
                                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                              disabled={regeneratingIds.has(img.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                                            >
                                              {regeneratingIds.has(img.id) ? (
                                                <>
                                                  <Spinner size="sm" className="mr-1" />
                                                  분석중...
                                                </>
                                              ) : (
                                                <>🔄 AI 재분석</>
                                              )}
                                            </Button>
                                          )}
                                          <Button
                                            onClick={() => handleDeleteImage(img.id)}
                                            variant="destructive"
                                            size="sm"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ) : (
                                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex flex-col gap-2">
                                    <p className="text-sm text-red-700">ALT 생성에 실패했습니다.</p>
                                    <Button
                                      onClick={() => handleRegenerateAlt(img.id)}
                                      size="sm"
                                      variant="outline"
                                      className="w-fit text-blue-600 border-blue-300 hover:bg-blue-50 whitespace-nowrap"
                                      disabled={regeneratingIds.has(img.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                                    >
                                      {regeneratingIds.has(img.id)
                                        ? <><Spinner size="xs" className="mr-1" />분석중...</>
                                        : <>🔄 AI 재시도</>}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}

                      {images.length > 0 && (
                        <div className="flex justify-between items-center mt-6">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span>페이지당</span>
                            <Select
                              value={String(pageSize)}
                              onValueChange={(v) => {
                                const newSize = Number(v);
                                setPageSize(newSize);
                                setCurrentPage(0);
                                fetchImages(0, filter === 'ALL' ? null : filter, newSize);
                              }}
                            >
                              <SelectTrigger className="w-20 h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="10">10개</SelectItem>
                                <SelectItem value="20">20개</SelectItem>
                                <SelectItem value="50">50개</SelectItem>
                                <SelectItem value="100">100개</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {totalPages > 1 && (
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={() => fetchImages(currentPage - 1, filter === 'ALL' ? null : filter)}
                                disabled={currentPage === 0}
                                variant="outline"
                              >
                                이전
                              </Button>
                              <span className="px-4 py-2 text-sm">
                                {currentPage + 1} / {totalPages}
                              </span>
                              <Button
                                onClick={() => fetchImages(currentPage + 1, filter === 'ALL' ? null : filter)}
                                disabled={currentPage >= totalPages - 1}
                                variant="outline"
                              >
                                다음
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
    </>
  );
}

export default AlttextImgUrl;
